/**
 * Leave Management Help Content
 *
 * Covers leave requests, approvals, balances, and work location.
 */

import type { HelpContent } from '../types';
import {
  createHelpContent,
  createSection,
  createFAQ,
  createTip,
  createWalkthrough,
  createTroubleshooting,
  createRelatedLink,
} from '../utils';

// =============================================================================
// MAIN LEAVE HELP
// =============================================================================

export const leaveHelp: HelpContent = createHelpContent({
  id: 'leave',
  title: 'Leave Management',
  description: 'Request and manage your time off, view balances, and track leave history',
  lastUpdated: '2026-02-08',
  version: '2.0',

  tips: [
    createTip(
      'tip-advance',
      'info',
      'Plan Ahead',
      'Submit vacation leave requests at least 2 weeks in advance to ensure smooth approval and team planning.'
    ),
    createTip(
      'tip-balance',
      'pro-tip',
      'Check Balances First',
      'Review your leave balances before submitting a request. You can see available days by type in the Balances section.'
    ),
  ],

  sections: [
    createSection(
      'overview',
      'Overview',
      `<p>The Leave Management system helps you request time off, track your leave balances, and view your leave history. All leave requests go through an approval workflow with your reporting manager.</p>
      <p>Key features include:</p>
      <ul>
        <li><strong>Leave Request</strong> — Submit new leave requests with type, dates, and notes</li>
        <li><strong>Leave Balances</strong> — View available, used, and pending days by leave type</li>
        <li><strong>Leave Calendar</strong> — Visual calendar showing your leave and team absences</li>
        <li><strong>Leave History</strong> — Track all past requests and their statuses</li>
      </ul>`
    ),
    createSection(
      'requesting',
      'Requesting Leave',
      `<p>To request leave:</p>
      <ol>
        <li>Select the leave type (vacation, sick, personal, etc.)</li>
        <li>Choose your start and end dates</li>
        <li>For partial days, select the half-day option if available</li>
        <li>Add any relevant notes for your manager</li>
        <li>Submit for approval</li>
      </ol>
      <p>Your reporting manager will be notified immediately and can approve, reject, or request changes.</p>`
    ),
    createSection(
      'balances',
      'Leave Balances',
      `<p>View your available leave balances by type. The balance breakdown shows:</p>
      <ul>
        <li><strong>Total Entitlement</strong> — Your allocated days for the year</li>
        <li><strong>Used</strong> — Days already taken</li>
        <li><strong>Pending</strong> — Days in submitted but not yet approved requests</li>
        <li><strong>Remaining</strong> — Available days (entitlement - used - pending)</li>
      </ul>
      <p>Balances reset according to your company's leave policy, typically at the start of the calendar or fiscal year.</p>`
    ),
    createSection(
      'calendar',
      'Leave Calendar',
      `<p>The leave calendar provides a visual view of:</p>
      <ul>
        <li>Your approved and pending leave days</li>
        <li>Team members' leave (for planning around absences)</li>
        <li>Public holidays in your holiday calendar</li>
      </ul>
      <p>Color coding helps distinguish between different leave types and statuses.</p>`
    ),
    createSection(
      'approval-workflow',
      'Approval Workflow',
      `<p>The leave approval process works as follows:</p>
      <ol>
        <li>You submit a leave request</li>
        <li>Your reporting manager receives a notification</li>
        <li>The manager reviews and approves, rejects, or requests changes</li>
        <li>You receive a notification of the decision</li>
        <li>Approved leave appears on your calendar and deducts from your balance</li>
      </ol>
      <p>Sick leave may have different approval rules — check your company policy.</p>`
    ),
    createSection(
      'history',
      'Leave History',
      '<p>View all your past leave requests with their statuses (approved, rejected, cancelled). Use filters to view specific time periods or leave types. This is useful for tracking patterns and planning future leave.</p>'
    ),
  ],

  walkthroughs: [
    createWalkthrough(
      'walkthrough-request',
      'How to Request Leave',
      'Submit a new leave request step by step.',
      [
        {
          title: 'Check your balance',
          description: 'Navigate to the Leave page and review your available balances for the leave type you want to use.',
        },
        {
          title: 'Click "New Leave Request"',
          description: 'Click the button to open the leave request form.',
        },
        {
          title: 'Select leave type',
          description: 'Choose the appropriate leave type from the dropdown (vacation, sick, personal, etc.).',
        },
        {
          title: 'Pick your dates',
          description: 'Select the start and end dates. For single-day leave, both dates will be the same.',
        },
        {
          title: 'Add notes (optional)',
          description: 'Add any context your manager might need for approval.',
        },
        {
          title: 'Submit',
          description: 'Click Submit. Your manager will be notified and your request will appear as "Pending".',
        },
      ],
      2
    ),
    createWalkthrough(
      'walkthrough-approve',
      'How to Approve Leave Requests (Managers)',
      'Review and approve team leave requests.',
      [
        {
          title: 'Navigate to Leave Approvals',
          description: 'Go to the Leave Approvals page to see pending requests from your team.',
        },
        {
          title: 'Review the request',
          description: 'Check the leave type, dates, and any notes. Consider team workload and coverage.',
        },
        {
          title: 'Check team calendar',
          description: 'Look at who else is off during the same period to ensure adequate coverage.',
        },
        {
          title: 'Approve or reject',
          description: 'Click Approve to grant the leave, or Reject with a reason if it cannot be accommodated.',
        },
      ],
      2
    ),
  ],

  troubleshooting: [
    createTroubleshooting(
      'trouble-balance',
      'Leave balance seems incorrect',
      [
        'Pending requests deducting from available balance',
        'Carryover from previous year not applied yet',
        'Leave type entitlement changed mid-year',
      ],
      [
        {
          description: 'Check pending requests',
          steps: [
            'Pending leave requests deduct from your available balance',
            'Cancel any unneeded pending requests to restore balance',
          ],
        },
        {
          description: 'Contact HR',
          steps: [
            'If balance still looks wrong after checking pending requests',
            'Contact HR with specific dates and expected balance for correction',
          ],
        },
      ]
    ),
    createTroubleshooting(
      'trouble-approval',
      'Leave request stuck in pending status',
      [
        'Manager has not yet reviewed the request',
        'Manager may be on leave themselves',
        'Notification may not have been delivered',
      ],
      [
        {
          description: 'Follow up directly',
          steps: [
            'Check if your manager is available (not on leave)',
            'Send a direct message or email reminding them of your request',
          ],
        },
        {
          description: 'Escalate if needed',
          steps: [
            'If your manager is unavailable for an extended period',
            'Contact HR to have the request reassigned to an alternate approver',
          ],
        },
      ]
    ),
    createTroubleshooting(
      'trouble-calendar-sync',
      'Leave not appearing on calendar',
      [
        'Leave not yet approved',
        'Page needs to be refreshed',
        'Calendar filter hiding the leave type',
      ],
      [
        {
          description: 'Verify approval status',
          steps: [
            'Check your leave history to confirm the request is approved',
            'Only approved leave appears on the calendar',
          ],
        },
        {
          description: 'Check calendar filters',
          steps: [
            'Ensure the leave type filter is enabled',
            'Refresh the page to load latest data',
          ],
        },
      ]
    ),
  ],

  faqs: [
    createFAQ(
      'faq-advance',
      'How far in advance should I request leave?',
      'Vacation leave should ideally be requested at least 2 weeks in advance. Sick leave can be submitted after the fact. Check your company policy for specific requirements by leave type.',
      ['advance', 'notice', 'timing', 'ahead']
    ),
    createFAQ(
      'faq-cancel',
      'Can I cancel a leave request?',
      'Yes, you can cancel pending or approved leave requests from your leave history. Cancelled days will be restored to your balance immediately.',
      ['cancel', 'withdraw', 'delete', 'undo']
    ),
    createFAQ(
      'faq-carryover',
      'What happens to unused leave at year end?',
      'Leave carryover policies vary by company and leave type. Some types may expire while others can roll over. Check your company\'s HR policy in the Knowledge Base for details.',
      ['carryover', 'expire', 'unused', 'rollover', 'year-end']
    ),
    createFAQ(
      'faq-half-day',
      'Can I take a half-day of leave?',
      'Yes, if your company supports half-day leave. When creating a request for a single day, look for the half-day toggle option to take just the morning or afternoon off.',
      ['half-day', 'partial', 'morning', 'afternoon']
    ),
    createFAQ(
      'faq-public-holidays',
      'Are public holidays deducted from my leave balance?',
      'No, public holidays are separate from your leave entitlement. If a public holiday falls during your leave period, it is not counted as a leave day.',
      ['public holiday', 'bank holiday', 'deduct', 'count']
    ),
    createFAQ(
      'faq-compulsory',
      'Can my manager require me to take leave?',
      'Some companies have mandatory leave policies (e.g., company shutdown periods). These will be communicated in advance and may be pre-applied to your calendar.',
      ['compulsory', 'mandatory', 'forced', 'shutdown']
    ),
    createFAQ(
      'faq-overlap',
      'What if my leave overlaps with a critical deadline?',
      'Discuss with your manager before submitting. If coverage is needed, coordinate with team members. Your manager may suggest alternative dates if the overlap is problematic.',
      ['overlap', 'conflict', 'deadline', 'coverage']
    ),
  ],

  relatedLinks: [
    createRelatedLink('Leave Approvals', '/leave-approvals', { description: 'Approve team leave requests (managers)' }),
    createRelatedLink('Work Location', '/work-location', { description: 'Declare your daily work location' }),
    createRelatedLink('Knowledge Base', '/knowledge', { description: 'View leave policies and HR guidelines' }),
    createRelatedLink('Dashboard', '/', { description: 'View pending leave on your dashboard' }),
  ],

  keywords: [
    'vacation',
    'time off',
    'PTO',
    'sick leave',
    'absence',
    'annual leave',
    'approval',
    'balance',
    'request',
    'holiday',
  ],
});

// =============================================================================
// SECTION-LEVEL HELP
// =============================================================================

export const leaveApprovalsHelp: HelpContent = createHelpContent({
  id: 'leave:approvals',
  title: 'Leave Approvals',
  description: 'Review and manage leave requests from your team',
  parentId: 'leave',
  sections: [
    createSection(
      'overview',
      'Overview',
      '<p>The Leave Approvals page shows all pending leave requests from your direct reports. Review, approve, or reject requests with a single click. You can also see a history of previously actioned requests.</p>'
    ),
    createSection(
      'reviewing',
      'Reviewing Requests',
      `<p>When reviewing a leave request, consider:</p>
      <ul>
        <li><strong>Team coverage</strong> — Who else is off during the same period?</li>
        <li><strong>Workload</strong> — Are there critical deadlines or releases?</li>
        <li><strong>Leave balance</strong> — Does the employee have sufficient balance?</li>
        <li><strong>Policy compliance</strong> — Does the request follow company policy?</li>
      </ul>`
    ),
    createSection(
      'actions',
      'Taking Action',
      '<p>For each pending request, you can: <strong>Approve</strong> to grant the leave, <strong>Reject</strong> with an optional reason, or leave it pending for later review. The employee is notified immediately of your decision.</p>'
    ),
  ],
  faqs: [
    createFAQ('faq-1', 'What if I\'m away and can\'t approve in time?', 'Contact HR to set up a temporary delegate or ask your own manager to cover approvals during your absence.', ['delegate', 'away', 'coverage']),
    createFAQ('faq-2', 'Can I approve leave retroactively?', 'Yes, sick leave and emergency leave can be submitted and approved after the fact.', ['retroactive', 'after', 'late']),
  ],
  keywords: ['approve', 'reject', 'pending', 'manager', 'team leave'],
  visibility: { visibleTo: ['manager', 'team_lead', 'admin'] },
});

export const leaveWorkLocationHelp: HelpContent = createHelpContent({
  id: 'leave:work-location',
  title: 'Work Location',
  description: 'Declare your daily work location for hybrid work tracking',
  parentId: 'leave',
  sections: [
    createSection(
      'overview',
      'Overview',
      '<p>The Work Location page lets you declare where you\'ll be working each day — Office, Home, or Client Site. This helps teams plan in-person meetings and track hybrid work patterns.</p>'
    ),
    createSection(
      'declaring',
      'Declaring Your Location',
      `<p>Set your work location for each day of the month:</p>
      <ul>
        <li>Click on a date in the calendar</li>
        <li>Select your location type (Office, Home, Client Site)</li>
        <li>You can set locations in advance for future dates</li>
        <li>Past dates can also be updated if needed</li>
      </ul>`
    ),
  ],
  faqs: [
    createFAQ('faq-1', 'Do I need to set my work location every day?', 'It\'s recommended but may depend on your company policy. Setting a default location can reduce daily updates.', ['daily', 'required', 'default']),
    createFAQ('faq-2', 'Can my manager see my work location?', 'Yes, managers can view team work locations to plan office days and meetings.', ['manager', 'visibility', 'team']),
  ],
  keywords: ['WFH', 'office', 'hybrid', 'remote', 'client site', 'location'],
});
